
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {
  Input,
  Select,
  Button,
  Icon,
  Flex,
  FlexItem,
  Margin,
  Size,
  FlexJustify,
  Category,
} from '@drawbotics/react-drylus';
import { css } from '@emotion/css';
import Playground from '~/components/Playground';
import { ControlledField } from '../utils';
export const style = {
  example: css`
    label: noreplace;
    width: 300px;
    > * {
      margin-bottom: 20px !important;
    }
  `
};
export const Example = ({
  children
}) => {
  return <div className={style.example}>
      {children}
    </div>;
};

const layoutProps = {
  style,
Example
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Input`}</h1>
<p>{`Usage`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { Input } from '@drawbotics/react-drylus';
`}</code></pre>
<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={0} __code={`<ControlledField initialValue="" style={{ maxWidth: 250 }} component={
  <Input placeholder="Placeholder..." onChange={x=>x} value="" />
} />`} component={Input} mdxType="Playground">
  <ControlledField initialValue="" style={{
        maxWidth: 250
      }} component={<Input placeholder="Placeholder..." onChange={x => x} value="" mdxType="Input" />} mdxType="ControlledField" />
</Playground>
<p>{`The `}<inlineCode parentName="p">{`type`}</inlineCode>{` property has been limited to a few possible from the HTML definition, since we have specific components for some types (see the rest of the Forms section). For each type you define, you can pass the native properties to customize it, e.g. for `}<inlineCode parentName="p">{`type="email"`}</inlineCode>{` you may pass `}<inlineCode parentName="p">{`pattern=".+@globex.com" size="30"`}</inlineCode></p>
<p>{`If no `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` value is passed, the field is automatically set to `}<inlineCode parentName="p">{`read-only`}</inlineCode>{`, which doesn't allow the value to be modified.`}</p>
<Playground __position={1} __code={`<Input value="The value is locked" style={{ maxWidth: 300 }} />`} mdxType="Playground">
  <Input value="The value is locked" style={{
        maxWidth: 300
      }} mdxType="Input" />
</Playground>
<p>{`You can also add a leading and trailing components.`}</p>


<Playground __position={2} __code={`<Example>
  <Input placeholder="Something..." leading="$" value="" onChange={x=>x} />
  <Input placeholder="Something..." leading={<Icon name="command" />} value="" onChange={x=>x} />
  <Input placeholder="Something..." trailing=".com" value="" onChange={x=>x} />
  <Input placeholder="Something..." leading={
    <Select value="option" options={[{ label: 'Option', value: 'option' }]} placeholder="Select..." onChange={x=>x} />
  } value="" onChange={x=>x} />
  <Input placeholder="Something else..." trailing={
    <Button leading={<Icon name="copy" />}>Click me</Button>
  } value="" onChange={x=>x} />
  <Input placeholder="Something else..." trailing={
    <Button category={Category.INFO}>Click me</Button>
  } value="" onChange={() => {}} />
  <Input placeholder="Something else..." leading={
    <Button category={Category.INFO}>Click me</Button>
  } value="" onChange={() => {}} />
</Example>`} mdxType="Playground">
  <Example mdxType="Example">
    <Input placeholder="Something..." leading="$" value="" onChange={x => x} mdxType="Input" />
    <Input placeholder="Something..." leading={<Icon name="command" mdxType="Icon" />} value="" onChange={x => x} mdxType="Input" />
    <Input placeholder="Something..." trailing=".com" value="" onChange={x => x} mdxType="Input" />
    <Input placeholder="Something..." leading={<Select value="option" options={[{
          label: 'Option',
          value: 'option'
        }]} placeholder="Select..." onChange={x => x} mdxType="Select" />} value="" onChange={x => x} mdxType="Input" />
    <Input placeholder="Something else..." trailing={<Button leading={<Icon name="copy" mdxType="Icon" />} mdxType="Button">Click me</Button>} value="" onChange={x => x} mdxType="Input" />
    <Input placeholder="Something else..." trailing={<Button category={Category.INFO} mdxType="Button">Click me</Button>} value="" onChange={() => {}} mdxType="Input" />
    <Input placeholder="Something else..." leading={<Button category={Category.INFO} mdxType="Button">Click me</Button>} value="" onChange={() => {}} mdxType="Input" />
  </Example>
</Playground>
<p>{`You can use the smaller size variant when space is scarce on the pace (this will be defined in the designs).`}</p>
<Playground __position={3} __code={`<Example>
  <Input value="Locked" size={Size.SMALL} />
  <Input valid onChange={() => {}} value="Some value" size={Size.SMALL} />
  <Flex justify={FlexJustify.START}>
    <FlexItem flex>
      <Margin size={{ right: Size.EXTRA_SMALL }}>
        <Input onChange={() => {}} placeholder="Type something..." size={Size.SMALL} />
      </Margin>
    </FlexItem>
    <FlexItem>
      <Button category={Category.INFO} size={Size.SMALL}>Click me</Button>
    </FlexItem>
  </Flex>
  <Input size={Size.SMALL} placeholder="Something..." leading={<Icon name="command" />} value="" onChange={x=>x} />
  <Input size={Size.SMALL} placeholder="Something..." trailing=".com" value="" onChange={() => {}} />
  <Input size={Size.SMALL} placeholder="Something..." leading={
    <Select value="option" options={[{ label: 'Option', value: 'option' }]} placeholder="Select..." onChange={x=>x} />
  } value="" onChange={() => {}} />
  <Input size={Size.SMALL} placeholder="Something else..." trailing={
    <Button leading={<Icon name="copy" />}>Click me</Button>
  } value="" onChange={() => {}} />
  <Input size={Size.SMALL} placeholder="Something else..." trailing={
    <Button category={Category.DANGER}>Click me</Button>
  } value="" onChange={() => {}} />
</Example>`} mdxType="Playground">
  <Example mdxType="Example">
    <Input value="Locked" size={Size.SMALL} mdxType="Input" />
    <Input valid onChange={() => {}} value="Some value" size={Size.SMALL} mdxType="Input" />
    <Flex justify={FlexJustify.START} mdxType="Flex">
      <FlexItem flex mdxType="FlexItem">
        <Margin size={{
              right: Size.EXTRA_SMALL
            }} mdxType="Margin">
          <Input onChange={() => {}} placeholder="Type something..." size={Size.SMALL} mdxType="Input" />
        </Margin>
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Button category={Category.INFO} size={Size.SMALL} mdxType="Button">Click me</Button>
      </FlexItem>
    </Flex>
    <Input size={Size.SMALL} placeholder="Something..." leading={<Icon name="command" mdxType="Icon" />} value="" onChange={x => x} mdxType="Input" />
    <Input size={Size.SMALL} placeholder="Something..." trailing=".com" value="" onChange={() => {}} mdxType="Input" />
    <Input size={Size.SMALL} placeholder="Something..." leading={<Select value="option" options={[{
          label: 'Option',
          value: 'option'
        }]} placeholder="Select..." onChange={x => x} mdxType="Select" />} value="" onChange={() => {}} mdxType="Input" />
    <Input size={Size.SMALL} placeholder="Something else..." trailing={<Button leading={<Icon name="copy" mdxType="Icon" />} mdxType="Button">Click me</Button>} value="" onChange={() => {}} mdxType="Input" />
    <Input size={Size.SMALL} placeholder="Something else..." trailing={<Button category={Category.DANGER} mdxType="Button">Click me</Button>} value="" onChange={() => {}} mdxType="Input" />
  </Example>
</Playground>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;